import { ChevronLeft, ShieldCheck, AlertTriangle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useActiveWorkspace, useActiveProfile, useSeed } from "@/domain/hooks";
import { Tooltip } from "@/design/components";
import { ImpactLedgerLogo } from "./ImpactLedgerLogo";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { ProfileSwitcher } from "./ProfileSwitcher";
import { NotificationsBell } from "./NotificationsDrawer";

const SOVEREIGNTY_THRESHOLD = 400_000_000;

function useSovereigntyAlert() {
  const ws = useActiveWorkspace();
  const seed = useSeed();
  if (!ws.compliancePackName.includes("Sovereignty")) return null;
  const near = seed.subProjects.filter((sp) => sp.spent >= SOVEREIGNTY_THRESHOLD * 0.85 && sp.spent < SOVEREIGNTY_THRESHOLD);
  const crossed = seed.subProjects.filter((sp) => sp.spent >= SOVEREIGNTY_THRESHOLD);
  if (crossed.length > 0) return {
    level: "bad" as const,
    label: "Cl. 21 required",
    tip: `${crossed.length} sub-project${crossed.length > 1 ? "s" : ""} exceeded the 400M UGX threshold — Clause 21 declaration is required`,
  };
  if (near.length > 0) return {
    level: "warn" as const,
    label: "Near 400M",
    tip: `${near.length} sub-project${near.length > 1 ? "s" : ""} approaching the Uganda Sovereignty Bill 400M UGX threshold`,
  };
  return null;
}

export function WorkspaceHeader() {
  const ws = useActiveWorkspace();
  const profile = useActiveProfile();
  const location = useLocation();
  const sovereigntyAlert = useSovereigntyAlert();
  const fromState = (location.state as { from?: string } | null)?.from;
  const portalLanding =
    profile.group === "funder" ? "/funder" : profile.group === "oversight" ? "/stratus" : profile.landing;
  const backTo = fromState ?? portalLanding;

  return (
    <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-line bg-card px-4 sm:px-6">
      {/* Left: back link → logo → programme name */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <Link
          to={backTo}
          aria-label="Back to all deals"
          className="focus-ring inline-flex min-h-11 shrink-0 items-center gap-0.5 rounded-md py-1 pr-1.5 text-sm font-medium text-mute transition-colors hover:text-ink"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">All deals</span>
        </Link>
        <span className="hidden h-6 w-px bg-line sm:inline" aria-hidden />
        <ImpactLedgerLogo />
        <span className="hidden h-6 w-px bg-line md:inline" aria-hidden />
        <span className="hidden truncate text-sm font-medium text-ink md:inline">{ws.programmeName}</span>
      </div>

      {/* Right: status badges → funder logo → switchers */}
      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
        {sovereigntyAlert && (
          <Tooltip label={sovereigntyAlert.tip}>
            <button
              type="button"
              aria-label={sovereigntyAlert.tip}
              className={`inline-flex h-[22px] items-center gap-1.5 rounded-full border px-2 text-[11px] font-medium transition-opacity active:opacity-70 ${
                sovereigntyAlert.level === "bad"
                  ? "border-bad/40 bg-badSoft text-bad"
                  : "border-warn/40 bg-warnSoft text-warn"
              }`}
            >
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
              {/* icon only on mobile/tablet; label from md - tap still shows
                  the full explanation via the Tooltip either way. */}
              <span className="hidden md:inline whitespace-nowrap">{sovereigntyAlert.label}</span>
            </button>
          </Tooltip>
        )}
        <CompliancePackBadge name={ws.compliancePackName} />
        {/* funder logo deferred to lg so sm/md don't overflow */}
        <span className="hidden h-6 w-px bg-line lg:inline" aria-hidden />
        <img src={ws.funder.logo} alt={ws.funder.name} className="hidden h-6 lg:block" />
        <NotificationsBell />
        <WorkspaceSwitcher />
        <ProfileSwitcher />
      </div>
    </header>
  );
}

/** Compact compliance chip. Icon only on mobile/tablet; label visible from lg. */
export function CompliancePackBadge({ name }: { name: string }) {
  return (
    <Tooltip label={name}>
      <button
        type="button"
        aria-label={name}
        className="inline-flex h-[22px] items-center gap-1.5 rounded-full border border-ok/40 bg-okSoft px-2 text-[11px] font-medium text-ok transition-opacity active:opacity-70"
      >
        <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="hidden lg:inline">{name.split(" + ")[0]}</span>
      </button>
    </Tooltip>
  );
}
